/**
 * RowEditorDrawer — slide-over panel for creating / editing a row.
 *
 * Coordinator-only fields:
 *   - `internal_only_notes` (kept editable; the server gates VIEW via
 *     `careplans.row.notes.view_internal`, so writers will see what
 *     they entered; non-permitted readers see it stripped).
 *
 * Validation is server-side; this component performs only light
 * client-side hints (required-field highlight per catalog).
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';

const SUPPORT_LEVELS = ['INDEPENDENT', 'PROMPT', 'SUPERVISION', 'PARTIAL_ASSIST', 'FULL_ASSIST'];
const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const FREQUENCIES = ['as_required', 'per_shift', 'daily', 'morning', 'afternoon', 'evening', 'night', 'weekly', 'monthly', 'custom'];

const TEXT_FIELDS = [
  ['observation_or_need', 'Observation / Need'],
  ['goal_or_desired_outcome', 'Goal / Desired Outcome'],
  ['intervention_or_staff_instruction', 'Intervention / Staff Instruction'],
];

export function RowEditorDrawer({ initial, onClose, onSubmit, sectionMeta }) {
  const [form, setForm] = useState({
    observation_or_need: '',
    goal_or_desired_outcome: '',
    intervention_or_staff_instruction: '',
    support_level: 'SUPERVISION',
    risk_level: 'LOW',
    frequency: 'as_required',
    escalation_required: false,
    escalation_instruction: '',
    portal_visible: !!sectionMeta?.portal_default,
    shift_visible: !!sectionMeta?.shift_default,
    internal_only_notes: '',
    ...(initial || {}),
  });

  const required = sectionMeta?.required_fields || [];
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    delete payload._ai_draft;
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="structured-row-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="w-[480px] max-w-[90vw] h-full bg-white shadow-xl overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold">{initial?.id ? 'Edit Row' : 'New Row'}</h4>
          {form._ai_draft && (
            <span className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
              [MOCK] AI draft — review before saving
            </span>
          )}
        </div>

        {TEXT_FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-xs font-medium">
              {label}
              {required.includes(k) && <span className="text-rose-600 ml-1">*</span>}
            </label>
            <textarea
              value={form[k] || ''}
              onChange={set(k)}
              rows={k === 'intervention_or_staff_instruction' ? 4 : 2}
              className={`mt-1 w-full text-sm border rounded px-2 py-1 ${
                required.includes(k) && !form[k] ? 'border-rose-300' : ''
              }`}
              data-testid={`row-field-${k}`}
            />
          </div>
        ))}

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-xs font-medium">Support level</label>
            <select value={form.support_level} onChange={set('support_level')}
                    className="mt-1 w-full text-sm border rounded px-2 py-1"
                    data-testid="row-field-support_level">
              {SUPPORT_LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">Risk level</label>
            <select value={form.risk_level} onChange={set('risk_level')}
                    className="mt-1 w-full text-sm border rounded px-2 py-1"
                    data-testid="row-field-risk_level">
              {RISK_LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">Frequency</label>
            <select value={form.frequency} onChange={set('frequency')}
                    className="mt-1 w-full text-sm border rounded px-2 py-1"
                    data-testid="row-field-frequency">
              {FREQUENCIES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="text-xs flex items-center gap-1.5">
            <input type="checkbox" checked={!!form.portal_visible} onChange={set('portal_visible')}
                   data-testid="row-field-portal_visible" />
            Portal visible
          </label>
          <label className="text-xs flex items-center gap-1.5">
            <input type="checkbox" checked={!!form.shift_visible} onChange={set('shift_visible')}
                   data-testid="row-field-shift_visible" />
            Shift visible
          </label>
          <label className="text-xs flex items-center gap-1.5">
            <input type="checkbox" checked={!!form.escalation_required} onChange={set('escalation_required')}
                   data-testid="row-field-escalation_required" />
            Escalation required
          </label>
        </div>

        {form.escalation_required && (
          <div>
            <label className="text-xs font-medium">Escalation instruction</label>
            <textarea value={form.escalation_instruction || ''} onChange={set('escalation_instruction')}
                      rows={2}
                      className="mt-1 w-full text-sm border rounded px-2 py-1"
                      data-testid="row-field-escalation_instruction" />
          </div>
        )}

        <div>
          <label className="text-xs font-medium">
            Internal-only notes <span className="text-[10px] opacity-60">(coordinator+ only)</span>
          </label>
          <textarea value={form.internal_only_notes || ''} onChange={set('internal_only_notes')}
                    rows={2}
                    placeholder="Not visible to participants/family or basic staff"
                    className="mt-1 w-full text-sm border rounded px-2 py-1"
                    data-testid="row-field-internal_only_notes" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button size="sm" variant="ghost" type="button" onClick={onClose}
                  data-testid="row-cancel">
            Cancel
          </Button>
          <Button size="sm" type="submit" data-testid="row-save">
            {initial?.id ? 'Save Changes' : 'Add Row'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default RowEditorDrawer;
